import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Bell, Settings, LogOut, FileText, Inbox, AlertCircle, Flag, Palette, FileStack, HelpCircle, Users, Zap, Lock, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import NotificationBell from '@/components/NotificationBell';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const userInitial = user?.first_name?.charAt(0).toUpperCase() || 'A';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">Virewire</h1>
            <span className="text-sm text-gray-600">COMMAND CENTER</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs font-bold text-green-600 bg-green-50 px-3 py-1 rounded-full">
              ● OPERATIONAL
            </span>
            {/* Notification Bell */}
            <NotificationBell />
            <button className="p-2 hover:bg-gray-100 rounded-full">
              <Settings className="w-5 h-5 text-gray-600" />
            </button>
            {/* User Menu Dropdown */}
            <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-200 cursor-pointer hover:opacity-80 transition">
                  <div className="text-right">
                    <p className="text-xs font-bold text-gray-900">{user?.first_name || 'Admin'} {user?.last_name || ''}</p>
                    <p className="text-xs text-gray-600">System Administrator</p>
                  </div>
                  <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {userInitial}
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-600" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b">
                  {user?.first_name} {user?.last_name}
                </div>
                <DropdownMenuItem asChild>
                  <a href="/admin/settings" className="cursor-pointer text-sm">
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-destructive cursor-pointer text-sm"
                  onClick={() => {
                    logout();
                    navigate('/admin/login');
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="flex flex-1">
        {/* Sidebar */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 p-6 flex flex-col overflow-y-auto flex-shrink-0">
          {/* Branding */}
          <div className="mb-8">
            <h1 className="text-xl font-bold text-gray-900">Virewire</h1>
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Command Center</p>
          </div>

          {/* OPERATIONS Section */}
          <div className="mb-8">
            <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wider mb-3">Operations</h3>
            <nav className="space-y-1">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/dashboard')
                    ? 'text-gray-900 bg-gray-200 hover:bg-gray-300'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="w-4 h-4 bg-gray-600 rounded-sm"></div>
                Dashboard
              </button>
              <button
                onClick={() => navigate('/admin/orders-jobs')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/orders-jobs')
                    ? 'text-gray-900 bg-gray-200 hover:bg-gray-300'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="w-4 h-4 border border-gray-400 rounded"></div>
                Orders / Jobs
              </button>
              <button
                onClick={() => navigate('/admin/reports')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/reports')
                    ? 'text-gray-900 bg-gray-200 hover:bg-gray-300'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="w-4 h-4 bg-gray-600 rounded-sm"></div>
                Reports
              </button>
              <button
                onClick={() => navigate('/admin/qa')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/qa')
                    ? 'text-gray-900 bg-gray-200 hover:bg-gray-300'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="w-4 h-4 bg-green-600 rounded-sm"></div>
                Quality Assurance
              </button>
              <button
                onClick={() => navigate('/admin/service-requests')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/service-requests')
                    ? 'text-gray-900 bg-gray-200 hover:bg-gray-300'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="w-4 h-4 bg-blue-600 rounded-sm"></div>
                Service Requests
              </button>
            </nav>
          </div>

          {/* MARKETPLACE Section */}
          <div className="mb-8">
            <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wider mb-3">Marketplace</h3>
            <nav className="space-y-1">
              <button
                onClick={() => navigate('/admin/customers')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/customers')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg">👥</span>
                Customers
              </button>
              <button 
                onClick={() => navigate('/admin/providers')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/providers') || isActive('/admin/provider-performance') || isActive('/admin/provider-compliance') || isActive('/admin/provider-payouts')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg">👔</span>
                Providers
              </button>
              <button 
                onClick={() => navigate('/admin/properties')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/properties') || isActive('/admin/property-detail')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg">🏢</span>
                Properties
              </button>

            </nav>
          </div>

          {/* FINANCE Section */}
          <div className="mb-8">
            <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wider mb-3">Finance</h3>
            <nav className="space-y-1">
              <button 
                onClick={() => navigate('/admin/billing')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/billing') || isActive('/admin/invoices')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <div className="w-4 h-4 bg-teal-600 rounded-sm"></div>
                Billing
              </button>
              <button 
                onClick={() => navigate('/admin/payouts')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/payouts')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
                Payouts
              </button>
              <button
                onClick={() => navigate('/admin/pricing-rules')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/pricing-rules')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 17v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.381z" clipRule="evenodd" />
                </svg>
                Pricing Rules
              </button>
              <button
                onClick={() => navigate('/admin/configuration-status')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/configuration-status') || isActive('/admin/pricing-config')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zm0 8a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" />
                </svg>
                Configuration Status
              </button>
            </nav>
          </div>

          {/* COMPLIANCE Section */}
          <div className="mb-8">
            <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wider mb-3">Compliance</h3>
            <nav className="space-y-1">
              <button
                onClick={() => navigate('/admin/provider-documents')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/provider-documents')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <FileText className="w-4 h-4" />
                Provider Documents
              </button>
              <button
                onClick={() => navigate('/admin/review-queue')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/review-queue')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Inbox className="w-4 h-4" />
                Review Queue
              </button>
              <button
                onClick={() => navigate('/admin/expiry-alerts')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/expiry-alerts')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <AlertCircle className="w-4 h-4" />
                Expiry Alerts
              </button>
              <button
                onClick={() => navigate('/admin/risk-flags')}
                className={`w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                  isActive('/admin/risk-flags')
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Flag className="w-4 h-4" />
                Risk Flags
              </button>
            </nav>
          </div>

          {/* CONTENT & CMS Section */}
          <div className="mb-8">
            <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wider mb-3">Content & CMS</h3>
            <nav className="space-y-1">
              <button onClick={() => navigate('/admin/notices')} className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                location.pathname === '/admin/notices' 
                  ? 'text-teal-700 bg-teal-50' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}>
                <Palette className="w-4 h-4" />
                Notices
              </button>
              <button onClick={() => navigate('/admin/website-pages')} className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                location.pathname === '/admin/website-pages' 
                  ? 'text-teal-700 bg-teal-50' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}>
                <FileStack className="w-4 h-4" />
                Website Pages
              </button>
              <button onClick={() => navigate('/admin/help-faqs')} className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                location.pathname === '/admin/help-faqs' 
                  ? 'text-teal-700 bg-teal-50' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}>
                <HelpCircle className="w-4 h-4" />
                Help & FAQs
              </button>
            </nav>
          </div>

          {/* SYSTEM MANAGEMENT Section */}
          <div className="mb-8">
            <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wider mb-3">System Management</h3>
            <nav className="space-y-1">
              <button 
                onClick={() => navigate('/admin/payments')} 
                className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/payments' 
                    ? 'text-teal-700 bg-teal-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
                Payments
              </button>
              <button 
                onClick={() => navigate('/admin/financial-dashboard')} 
                className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/financial-dashboard' 
                    ? 'text-teal-700 bg-teal-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm8-7a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" clipRule="evenodd" />
                </svg>
                Financial Dashboard
              </button>
              <button 
                onClick={() => navigate('/admin/provider-onboarding')} 
                className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/provider-onboarding' 
                    ? 'text-teal-700 bg-teal-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg">📋</span>
                Provider Onboarding
              </button>
              <button 
                onClick={() => navigate('/admin/provider-support')} 
                className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/provider-support' 
                    ? 'text-teal-700 bg-teal-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg">🎯</span>
                Provider Support
              </button>
              <button 
                onClick={() => navigate('/admin/provider-analytics')} 
                className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/provider-analytics' 
                    ? 'text-teal-700 bg-teal-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span className="text-lg">📊</span>
                Provider Analytics
              </button>
            </nav>
          </div>

          {/* SETTINGS Section */}
          <div className="mb-8">
            <h3 className="text-xs uppercase font-bold text-gray-400 tracking-wider mb-3">Settings</h3>
            <nav className="space-y-1">
              <button 
                onClick={() => navigate('/admin/users')} 
                className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/users' 
                    ? 'text-teal-700 bg-teal-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Users className="w-4 h-4" />
                Admin Users
              </button>
              <button 
                onClick={() => navigate('/admin/platform-rules')} 
                className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/platform-rules'
                    ? 'text-teal-700 bg-teal-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Settings className="w-4 h-4" />
                Platform Rules
              </button>
              <button 
                onClick={() => navigate('/admin/permissions')} 
                className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/permissions' 
                    ? 'text-teal-700 bg-teal-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Lock className="w-4 h-4" />
                Permissions
              </button>
              <button
                onClick={() => navigate('/admin/notifications')}
                className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/notifications' || location.pathname === '/admin/notifications-settings'
                    ? 'text-teal-700 bg-teal-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Bell className="w-4 h-4" />
                Notifications
              </button>
              <button 
                onClick={() => navigate('/admin/integrations')} 
                className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                  location.pathname === '/admin/integrations' 
                    ? 'text-teal-700 bg-teal-50' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <Zap className="w-4 h-4" />
                Integrations
              </button>
            </nav>
          </div>

          {/* Logout Button - At Bottom */}
          <div className="mt-auto pt-8 border-t border-gray-200">
            <button
              onClick={() => {
                logout();
                navigate('/admin/login');
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
